import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  availableInstancesAt,
  heaviestWeightedSet,
  resolveInstanceChoice,
  type KnownInstance,
  type WorkoutExerciseEntry,
} from '@heraion/shared';

import { getInstanceSurveyForSlug } from './useEntryHistory';
import {
  buildWorkoutSetFromDraft,
  completedDraftSetsFor,
} from './workoutPayload';
import type { DraftEntry } from './state/loggerDraftTypes';
import { mintedInstances } from './_mintedInstances';

// The load signature of a machine used in the session that is still open.
//
// ⭐ Why this exists (Antenore, 2026-08-13 sera): the signature is what makes
// two machines tellable apart, and it was derived from SAVED sessions only —
// so the first time you coin a machine and load it, the option still reads as
// a bare "Machine A". You had to save, come back a second day, and only then
// did the feature start working. That is not a limitation of the idea, it is
// an accident of where the data was read from: the sets you just logged
// describe the machine in front of you exactly as well as yesterday's do.
//
// ⛔ Still NOT a parallel registry (§9.5.2). Nothing is stored: this reads the
// draft the athlete is looking at, converts its COMPLETED sets with the same
// builder the save path uses (so the numbers match what will be written), and
// hands them to the same shared derivation. A machine still exists only
// because it was logged.
//
// `instance` is nullable because the DEFAULT is an instance like any other
// here (EQ-1-DEFAULT): `DraftEntry.equipmentInstance` is already `string |
// null`, so the comparison below answers "the sets logged on the unnamed one"
// with no branch of its own.
const liveSignature = (
  entries: readonly DraftEntry[],
  exerciseId: string,
  instance: string | null,
): { weightKg: number; reps: number } | undefined => {
  const sets = [];
  for (const { entry, set } of completedDraftSetsFor(entries, exerciseId)) {
    if (entry.equipmentInstance !== instance) continue;
    const built = buildWorkoutSetFromDraft(set, entry.isBodyweight);
    if (built) sets.push(built);
  }
  if (sets.length === 0) return undefined;
  return heaviestWeightedSet({ sets } as WorkoutExerciseEntry);
};

// EQ-1 — "does this exercise need to be asked which machine, and what do we
// call the answer?" asked once, for the two surfaces that ask it: the swap
// picker's rows and its current-exercise line.
//
// It exists because those two derived it identically the day they were both
// written. Two copies of a rule that decides whether the athlete is prompted
// is the kind that drifts quietly: one surface would start asking where the
// other stopped, and nothing would fail.
// ⚠️ The second argument is an OBJECT, not two positionals. Slice D wanted to
// add the place beside the draft instance and both are `string | null` — so a
// caller swapping them would compile clean and behave wrongly, ranking the
// list by a machine name and treating a gym as the current machine. Naming
// them makes that impossible rather than unlikely.
export function useMachineChoice(
  exerciseId: string,
  opts: {
    /**
     * The instance the entry being edited already carries, if any.
     *
     * ⛔ Without this the feature loses the machine you just created. The
     * known list is derived from SAVED sessions (no parallel registry, one
     * truth — §9.5.2), and a machine minted mid-workout lives only in the
     * draft, so it vanished from the picker until the workout was saved.
     * Antenore hit it on the second visit to the picker and read the cause
     * correctly. The union here keeps the no-registry rule intact: the draft
     * is not a second store of machines, it is simply the one session the
     * history does not have yet.
     */
    draftInstance?: string | null;
    /**
     * Where the session is happening, when it has been said (EQ-1 slice D).
     *
     * ⛔ It RANKS, it never trims: the machines of this gym lead, everything
     * else stays exactly where it was, one scroll down. A place that hid a
     * machine would be the first-instance dead end again — you stand in front
     * of a machine the app has decided is not here, with no way forward.
     */
    orderByLocation?: string | null;
    /**
     * The entries of the session still open, so a machine coined and loaded
     * TODAY already carries its load signature. Without it the feature only
     * starts describing machines from the second session onwards.
     */
    liveEntries?: readonly DraftEntry[];
    /**
     * EQ-1-GYM-COLDSTART R6 — the machines the athlete DECLARED absent here,
     * already resolved by the caller (`declaredAbsentInstancesAt`).
     *
     * ⛔ They are not offered. This is the one narrowing the tray accepts, and
     * it is deliberately NOT the treatment the exercise LIST gets: there a
     * declared-absent exercise sinks and stays tappable, because the athlete
     * may still want it. A machine is different — picking one STAMPS an
     * identity on the set, and stamping a machine that is not in the room
     * corrupts the per exercise+tag history this whole feature exists to keep
     * clean. An exercise you do anyway is a choice; a machine that is not
     * there is not a choice, it is a wrong answer.
     *
     * ⛔ It never empties the tray: the Default and "New machine" are
     * unconditional, so there is always a way forward and always a way back to
     * no tag. The declaration itself is undone from the ✕ on the same row.
     */
    absentHere?: readonly string[];
    /**
     * EQ-1-PIN-DEFAULT — what the athlete ELECTED for this exercise here.
     *
     * ⛔ Passed here as well as on the silent path, and that is deliberate
     * despite the ⚠️ on `resolveInstanceChoice`'s `defaultCarriesHistory`.
     * That warning is about a flag which fires on nearly every exercise (any
     * untagged past) and would therefore grow a prompt nobody asked for. This
     * one is the opposite animal: it exists ONLY where the athlete personally
     * elected a winner, and its whole purpose is to REMOVE a prompt. Reading
     * the caution as covering both would leave the interruption this feature
     * was built to end.
     *
     * Three-valued like its source: a slug, `null` for the virtual Default,
     * `undefined` for "nothing elected".
     */
    pinned?: string | null;
  } = {},
): {
  known: KnownInstance[];
  /**
   * The default machine's history (EQ-1-DEFAULT). Always present — the default
   * is offered whether or not anything was ever logged untagged.
   *
   * ⛔ Kept OUT of `known` all the way to the tray, because `asks` reads
   * `known` alone: an exercise with one named machine keeps applying it in
   * silence instead of growing a prompt nobody decided to add.
   */
  fallback: Omit<KnownInstance, 'value'>;
  /** True only in the `choose` case — two or more plausible machines. */
  asks: boolean;
  /**
   * How many options the tray will actually offer, default included.
   *
   * ⚠️ NOT `known.length`. The badge and the spoken label exist so that a row
   * that will expand says so before the tap, and they were both undercounting
   * by one the moment the default became a permanent option — a number that is
   * read instead of the glyph by anyone with reduced vision, and it was wrong.
   */
  optionCount: number;
  /** Spoken count, for the host's accessibility label. */
  countLabel: string;
  /**
   * Every value already spoken for, hidden ones included — what a NEW machine
   * must not be named.
   *
   * ⚠️ Not `known.map(v)` any more, and that is the trap this exists to close:
   * `nextAutoInstanceValue` picks the next free letter from what it is shown,
   * so naming a machine off the NARROWED list would hand back `machine-a`
   * again the moment `machine-a` was declared absent — two different machines
   * sharing one identity, and their histories silently merged. The narrowing
   * is about what may be CHOSEN, never about what a name may collide with.
   */
  takenValues: string[];
} {
  const { t } = useTranslation();
  const { draftInstance, orderByLocation, liveEntries, absentHere, pinned } =
    opts;

  // Derived once per exercise rather than per render: it walks the cached
  // history window, and the picker re-renders on every keystroke in search.
  const survey = useMemo(() => {
    const fromHistory = getInstanceSurveyForSlug(
      exerciseId,
      orderByLocation ?? undefined,
    );
    const bySaved = new Map(fromHistory.named.map((k) => [k.value, k]));

    // The live session wins when it has one: it is the most recent thing that
    // happened on that machine, which is the same rule the saved window
    // already follows. Stated once so the named machines and the default
    // cannot answer it differently — the default is the one the athlete has
    // used most, so a signature that lagged there would lag where it matters.
    const withLive = <T extends { topSet?: KnownInstance['topSet'] }>(
      base: T,
      instance: string | null,
    ): T => {
      const live = liveEntries
        ? liveSignature(liveEntries, exerciseId, instance)
        : undefined;
      return live ? { ...base, topSet: live } : base;
    };

    // Order: the one in use, then the ones coined this session, then saved
    // history. Everything ahead of history is there precisely because history
    // does not know about it yet, and carries no signature until a set on it
    // has been saved.
    const ordered = [
      ...(draftInstance ? [draftInstance] : []),
      ...mintedInstances(exerciseId),
      ...fromHistory.named.map((k) => k.value),
    ];

    const seen = new Set<string>();
    const known: KnownInstance[] = [];
    for (const value of ordered) {
      if (seen.has(value)) continue;
      seen.add(value);
      const saved = bySaved.get(value);
      known.push(
        withLive(saved ?? { value, lastUsedAt: '', sessions: 0 }, value),
      );
    }

    // The default is offered even when nothing has ever been logged without a
    // tag — that is the P0: on an exercise whose only history is the machine
    // just coined by mistake, an option that appeared only with history would
    // still leave no way back.
    const fallback = withLive(
      fromHistory.unnamed ?? { lastUsedAt: '', sessions: 0 },
      null,
    );
    return { known, fallback };
  }, [exerciseId, draftInstance, orderByLocation, liveEntries]);

  const { known: allKnown, fallback } = survey;
  // R6 — what the athlete declared absent here is not offered. Applied AFTER
  // the survey is built (rather than inside it) so `takenValues` below still
  // sees the whole set: the two lists answer two different questions.
  // ⛔ No exemption for the instance the entry currently carries. Hiding it
  // costs nothing legible — the chip above the tray names it either way — and
  // an exemption would put a machine declared absent back in front of the one
  // thumb most likely to re-pick it.
  const known = absentHere?.length
    ? availableInstancesAt(allKnown, absentHere)
    : allKnown;
  // The default is always one of them, so this is `+ 1` and not a conditional:
  // <MachineTray> renders it unconditionally.
  const optionCount = known.length + 1;
  return {
    known,
    fallback,
    takenValues: allKnown.map((k) => k.value),
    // ⛔ `known` alone, default excluded — see the return type. This decides
    // whether the athlete is INTERRUPTED; `optionCount` describes what the
    // tray holds. Two different questions, and folding them would turn a
    // description into a prompt.
    // EQ-1-PIN-DEFAULT — an election ends the question. Checked against the
    // NARROWED list so a machine elected and later declared absent here does
    // not silence a prompt that should still be asked; `null` (the Default,
    // elected) silences it outright, which is the case that matters most —
    // two machines known is exactly when the resolver interrupts every time.
    asks:
      pinned === null ||
      (pinned !== undefined && known.some((k) => k.value === pinned))
        ? false
        : resolveInstanceChoice(known).kind === 'choose',
    optionCount,
    countLabel: t('swap.machineChoices', {
      count: optionCount,
      defaultValue: `${optionCount} machines known`,
    }),
  };
}
